import { useAuth } from '@clerk/clerk-react';
import {
  Select,
  type SelectProps,
  type SelectItem,
  useSelectQueryGate,
} from '@vrplatform/components';
import {
  type gqlV2,
  getRecentTeamIdsStorageKey,
  parseStoredRecentTeamIds,
  useDashboard,
  useInfiniteQuery,
  useMe,
  useOwnerPortalContact,
  useQuery,
  useTeam,
  useTeamIdContext,
  useTeamRole,
  useTeamSwitch,
} from '@vrplatform/data';
import { ChevronIcon, Icon, PlusIcon } from '@vrplatform/icons';
import { IconButton, Logo, VrpDarkLogo } from '@vrplatform/components-legacy';
import { useAppName } from '@vrplatform/theme';
import {
  parseAsBoolean,
  parseAsString,
  useQueryStates,
} from '@vrplatform/query-params';
import { hasValue, isUUID, sortBy } from '@vrplatform/utils';
import {
  Box,
  Center,
  Group,
  Indicator,
  Stack,
  Text,
  Tooltip,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { openSpotlight } from '@mantine/spotlight';
import { VRP_TENANT_ID, formatOwnerName } from '@vrplatform/ui-common';
import { forwardRef, type JSX, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useNavbarExpanded } from '../../../hooks';
import {
  buildNavbarTeamSwitchVisibleData,
  groupNavbarOwnerSwitchItems,
  MIN_NAVBAR_TEAM_SWITCH_GROUPED_TEAM_COUNT,
  resolveNavbarTeamSwitchValue,
} from './NavbarTeamSwitch.utils';

export const NavbarTeamSwitch = () => {
  const { isDesktopExpanded } = useNavbarExpanded();

  const [dashboard] = useDashboard();

  return (
    <Group
      wrap="nowrap"
      justify={isDesktopExpanded ? 'flex-start' : 'center'}
      sx={{
        flex: 1,
        flexDirection: isDesktopExpanded ? undefined : 'column',
      }}
    >
      <DashboardLogo />
      {dashboard === 'owner' ? <OwnerSwitch /> : <TeamSwitch />}
    </Group>
  );
};

const DashboardLogo = () => {
  const { isDesktopExpanded } = useNavbarExpanded();
  const { isVrpAdmin } = useTeamRole();

  const [team] = useTeam();
  const [dashboard] = useDashboard();
  const logo = dashboard === 'owner' ? team.logo : undefined;

  const { white } = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();

  return (
    <Group
      wrap="nowrap"
      sx={{
        flexShrink: 0,
        justifyContent: isDesktopExpanded ? 'flex-start' : 'center',
        flexDirection: isDesktopExpanded ? undefined : 'column',
      }}
      w={40}
    >
      <Box
        onClick={() => isVrpAdmin && openSpotlight()}
        sx={{
          backgroundColor: colorScheme === 'dark' ? undefined : white,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 5,
          borderRadius: 10,
          cursor: isVrpAdmin ? 'pointer' : undefined,
        }}
      >
        {colorScheme === 'dark' && dashboard !== 'owner' ? (
          <VrpDarkLogo />
        ) : (
          <Logo img={logo} />
        )}
      </Box>
    </Group>
  );
};

function useTeamQuery(
  search: string,
  {
    currentTeamId,
    recentTeamIds = [],
    shouldFetchOptions = true,
  }: {
    currentTeamId?: string | null;
    recentTeamIds?: string[];
    shouldFetchOptions?: boolean;
  } = {}
) {
  const { id: meId } = useMe();
  const { isVrpAdmin } = useTeamRole();

  const getTeamSelectItem = useCallback(
    (team: gqlV2.tenant): SelectItem => {
      const isGL = !!team.isGeneralLedger;
      const name = team.name || '-';
      const isInactive = team.status === 'inactive';
      const isPartnerTeam = ['admin', 'partner'].includes(team.type || '');
      const isVrpBillingPartner =
        team.calculatedBillingPartnerId === VRP_TENANT_ID;

      return {
        value: team.id,
        label: name,
        icon:
          isGL && isVrpAdmin ? (
            <Center
              sx={{
                borderRadius: '100%',
              }}
            >
              <Tooltip label="VRTrust">
                <Indicator disabled={!isInactive} color="orange" size={6}>
                  <Box>
                    <Logo width={14} />
                  </Box>
                </Indicator>
              </Tooltip>
            </Center>
          ) : isInactive ? (
            <Tooltip label={isVrpBillingPartner ? 'Cancelled' : 'Suspended'}>
              <Icon icon="Lock2Icon" size={14} />
            </Tooltip>
          ) : isPartnerTeam ? (
            <Tooltip label="Partner team">
              <Icon icon="UsersIcon" size={14} />
            </Tooltip>
          ) : undefined,
      };
    },
    [isVrpAdmin]
  );

  const queryData = useInfiniteQuery(
    (q, { search, VRP_TENANT_ID }, { limit, offset }) => {
      const where: gqlV2.tenant_bool_exp = {
        status: { _neq: 'deleted' },
        type: { _nin: ['partner', 'admin'] },
        id: { _neq: VRP_TENANT_ID },
      };
      if (search) {
        where._or = [
          {
            name: { _ilike: `%${search}%` },
          },
        ];
        if (isUUID(search))
          where._or?.push({
            id: {
              _eq: search,
            },
          });
      }

      const aggregate = q.tenantAggregate({ where })?.aggregate?.count() || 0;

      const list = q
        .tenant({
          order_by: [{ type: 'asc_nulls_last' }, { name: 'asc' }],
          where,
          limit: limit,
          offset,
        })
        .map<SelectItem>(getTeamSelectItem);

      return {
        list,
        aggregate,
      };
    },
    {
      queryKey: 'teams',
      skip: !shouldFetchOptions,
      variables: {
        isVrpAdmin,
        search: search?.trim(),
        VRP_TENANT_ID,
      },
      teamScoped: false,
    }
  );

  const { data, isLoading: loading2 } = useQuery(
    (q, args) => {
      const recentTeamsById =
        args.recentTeamIds.length > 0
          ? q
              .tenant({
                where: {
                  status: { _neq: 'deleted' },
                  id: { _in: args.recentTeamIds },
                },
              })
              .map<SelectItem>(getTeamSelectItem)
          : [];

      const recentTeamsLookup = new Map(
        recentTeamsById.map((item) => [item.value, item] as const)
      );

      const recentTeams = args.recentTeamIds
        .map((teamId) => recentTeamsLookup.get(teamId))
        .filter((item): item is SelectItem => !!item);

      const currentTeam = args.currentTeamId
        ? (q
            .tenant({
              where: {
                status: { _neq: 'deleted' },
                id: { _eq: args.currentTeamId },
              },
              limit: 1,
            })
            .map<SelectItem>(getTeamSelectItem)[0] ?? null)
        : null;

      const switchableTeamGroupingProbe = q
        .tenant({
          where: {
            status: { _neq: 'deleted' },
            id: { _neq: args.VRP_TENANT_ID },
          },
          limit: MIN_NAVBAR_TEAM_SWITCH_GROUPED_TEAM_COUNT,
        })
        .map((team) => team.id);

      const canSwitch =
        q
          .tenant({ where: { status: { _neq: 'deleted' } }, limit: 3 })
          .map((x) => x.id).length > 1;

      const pinnedTeams = q
        .tenant({
          where: {
            status: { _neq: 'deleted' },
            type: { _in: ['admin', 'partner'] },
            id: { _neq: args.VRP_TENANT_ID },
          },
          order_by: [{ name: 'asc' }],
        })
        .map<SelectItem>(getTeamSelectItem);

      const isMultiplePartnerAdmin =
        q
          .tenantUser({
            where: {
              tenant: { type: { _eq: 'partner' } },
              role: { _eq: 'admin' },
              userId: { _eq: args.meId },
            },
            limit: 2,
          })
          .map((x) => x.id).length > 1;

      const isInMultipleTeams =
        q
          .tenantUser({
            where: {
              tenant: { type: { _neq: 'partner' } },
              userId: { _eq: args.meId },
            },
            limit: 2,
          })
          .map((x) => x.id).length > 1;

      return {
        canSwitch,
        currentTeam,
        pinnedTeams,
        recentTeams,
        shouldGroupTeams:
          switchableTeamGroupingProbe.length >=
          MIN_NAVBAR_TEAM_SWITCH_GROUPED_TEAM_COUNT,
        showTeamOverview: isMultiplePartnerAdmin || isInMultipleTeams,
      };
    },
    {
      queryKey: 'teams',
      keepPreviousData: true,
      variables: {
        isVrpAdmin,
        meId,
        currentTeamId,
        recentTeamIds,
        VRP_TENANT_ID,
      },
      teamScoped: false,
    }
  );

  const shouldGroupTeams = !!data?.shouldGroupTeams;

  const visibleData = useMemo(
    () =>
      buildNavbarTeamSwitchVisibleData({
        allTeamsPrefixItems: data?.pinnedTeams || [],
        pages: queryData.data?.pages || [],
        recentTeamIds,
        recentTeams: data?.recentTeams || [],
        search,
        shouldGroupTeams,
      }),
    [
      data?.pinnedTeams,
      data?.recentTeams,
      queryData.data?.pages,
      recentTeamIds,
      search,
      shouldGroupTeams,
    ]
  );

  const filteredQueryData = useMemo(() => {
    if (!queryData.data) return queryData;

    return {
      ...queryData,
      data: {
        ...queryData.data,
        pages: visibleData.pages,
      },
    };
  }, [queryData, visibleData.pages]);

  return {
    queryData: filteredQueryData,
    loadingValue: loading2,
    canSwitch: !!data?.canSwitch,
    currentTeam: data?.currentTeam ?? null,
    pinnedItems: visibleData.pinnedItems,
    // showTeamOverview: !!data?.showTeamOverview && OVERVIEW_UI,
  };
}

type TeamSwitchProps = {
  header?: string | null;
  hideWhenUnavailable?: boolean;
  isDesktopExpanded?: boolean;
  renderTrigger?: (props: NavbarTeamSwitchTriggerProps) => JSX.Element;
  tone?: 'navbar' | 'default';
};

export const TeamSwitch = ({
  header,
  hideWhenUnavailable = false,
  isDesktopExpanded,
  renderTrigger,
  tone = 'navbar',
}: TeamSwitchProps = {}) => {
  const [searchParams] = useSearchParams();
  const [, setDrawer] = useQueryStates({
    'add-team-drawer': parseAsBoolean,
    'add-team-name': parseAsString,
  });

  const openAddTeamModal = (teamName: string | undefined) => {
    setDrawer({
      'add-team-drawer': true,
      'add-team-name': teamName,
    });
  };

  const [team] = useTeam();
  const { teamId: selectedTeamId } = useTeamIdContext();
  const { id: meId } = useMe();
  const { isPartnerAdmin, isVrpAdmin } = useTeamRole();
  const setTeamId = useTeamSwitch();
  const goto = useNavigate();

  const [dashboard, toggleTeamOverview] = useDashboard();
  const activeTeamId = selectedTeamId ?? team.id;

  const [search, setSearch] = useState('');
  const recentTeamIds = useMemo(() => {
    if (!meId) return [];

    return parseStoredRecentTeamIds(
      globalThis.localStorage?.getItem(getRecentTeamIdsStorageKey(meId)) ?? null
    ).filter((teamId) => teamId !== VRP_TENANT_ID);
  }, [meId, team.id]);

  const gate = useSelectQueryGate({ value: activeTeamId });
  const { queryData, loadingValue, canSwitch, currentTeam, pinnedItems } =
    useTeamQuery(search, {
      currentTeamId: activeTeamId,
      recentTeamIds,
      shouldFetchOptions: gate.shouldFetchOptions,
    });
  const resolvedHeader =
    header === undefined
      ? isVrpAdmin
        ? team.partnerName || 'Team'
        : 'Team'
      : header;

  const value = useMemo(() => {
    return resolveNavbarTeamSwitchValue({
      dashboard,
      currentTeam,
      hydratedTeamId: team.id,
      hydratedTeamName: team.name,
      selectedTeamId,
    });
  }, [dashboard, currentTeam, team.id, team.name, selectedTeamId]);

  const setNewTeamValue = useCallback(
    (newValue: SelectItem | null) => {
      if (!newValue?.value) return;
      setTeamId(newValue?.value);

      const hasKeys = [...searchParams.values()].filter(hasValue).length > 0;
      if (hasKeys) {
        goto(window.location.pathname);
      }
      toggleTeamOverview(false);
    },
    [setTeamId, toggleTeamOverview, searchParams, goto]
  );

  const customActionTop = useMemo(
    () => ({
      label: 'VRPlatform',
      icon: (
        <Center
          sx={{
            borderRadius: '100%',
            overflow: 'hidden',
          }}
        >
          <Logo width={16} />
        </Center>
      ),
      onSubmit: () =>
        setNewTeamValue({
          label: 'VRPlatform',
          value: VRP_TENANT_ID,
        }),
    }),
    [setNewTeamValue]
  );

  if (hideWhenUnavailable && !canSwitch && !value) {
    return null;
  }

  return (
    <NavbarTeamSwitchSelect
      value={value}
      canSwitch={canSwitch}
      pinnedItems={pinnedItems}
      customActionTop={isVrpAdmin ? customActionTop : undefined}
      customBottomActions={
        isPartnerAdmin || isVrpAdmin
          ? [
              {
                label: 'Add team',
                icon: <PlusIcon />,
                onSubmit: (v) => openAddTeamModal(v),
                id: 'add-team',
              },
            ]
          : undefined
      }
      loadingQuery={loadingValue}
      onOpenChange={gate.onOpenChange}
      queryData={queryData}
      header={resolvedHeader}
      isDesktopExpanded={isDesktopExpanded}
      renderTrigger={renderTrigger}
      tone={tone}
      setSearch={setSearch}
      setValue={setNewTeamValue}
    />
  );
};

const getOwner = (x: gqlV2.owner_user_access) => {
  const ownerName = formatOwnerName(x.owner, { showEmpty: true });
  const contactName = formatOwnerName(x.contact, { showEmpty: true });

  const ownerTeamName = x.owner?.tenant?.name;
  const contactTeamName = x.contact?.tenant?.name;
  const isGeneralLedger =
    x.owner?.tenant?.isGeneralLedger ?? x.contact?.tenant?.isGeneralLedger;

  const ownerLogo = x.owner?.tenant?.logo;
  const contactLogo = x.contact?.tenant?.logo;
  const logo = ownerLogo || contactLogo;

  const contactId = x.contactId;
  const ownerId = x.ownerId;

  const id = contactId || ownerId || null;

  const tenantId = x.owner?.tenantId || x.contact?.tenantId || '-';
  const productGroup =
    isGeneralLedger === true
      ? 'VR Trust'
      : isGeneralLedger === false
        ? 'VR Platform'
        : 'Other';

  return {
    value: id!,
    label: ownerName || contactName || '-',
    description: ownerTeamName || contactTeamName,
    icon: (
      <Center w={26}>
        <Logo img={logo} width={26} radius="xl" />
      </Center>
    ),
    tenantId,
    productGroup,
  } satisfies SelectItem & {
    tenantId: string;
    productGroup: 'VR Trust' | 'VR Platform' | 'Other';
  };
};

function useOwnerQuery(
  search: string,
  {
    shouldFetchOptions,
    isUserPreview,
    teamId,
    isPreviewTeamSwitchPending,
  }: {
    shouldFetchOptions: boolean;
    isUserPreview: boolean;
    teamId: string | null | undefined;
    isPreviewTeamSwitchPending: boolean;
  }
) {
  const { id: meId } = useMe();
  const { contactId } = useOwnerPortalContact();

  const { data: valueData, isLoading: loadingValue } = useQuery(
    (q, args) => {
      if (!args.contactId)
        return {
          contact: null,
          canSwitch: false,
        };
      if (
        args.isUserPreview &&
        (args.isPreviewTeamSwitchPending || !args.teamId)
      )
        return {
          contact: null,
          canSwitch: false,
        };

      const whereForCurrentContact =
        args.isUserPreview && args.teamId
          ? {
              userId: { _eq: args.meId },
              _or: [
                {
                  ownerId: { _eq: args.contactId },
                  owner: {
                    tenantId: { _eq: args.teamId },
                  },
                },
                {
                  contactId: { _eq: args.contactId },
                  contact: {
                    tenantId: { _eq: args.teamId },
                  },
                },
              ],
            }
          : {
              userId: { _eq: args.meId },
              _or: [
                {
                  ownerId: { _eq: args.contactId },
                },
                {
                  contactId: { _eq: args.contactId },
                },
              ],
            };

      const whereForSwitching =
        args.isUserPreview && args.teamId
          ? {
              userId: { _eq: args.meId },
              _or: [
                {
                  owner: {
                    tenantId: { _eq: args.teamId },
                  },
                },
                {
                  contact: {
                    tenantId: { _eq: args.teamId },
                  },
                },
              ],
            }
          : {
              userId: { _eq: args.meId },
            };

      return {
        contact:
          q
            .ownerUserAccesses({
              where: whereForCurrentContact,
            })
            .map(getOwner)[0] ?? null,
        canSwitch:
          q
            .ownerUserAccesses({
              where: whereForSwitching,
              limit: 2,
            })
            .map((x) => x.id).length > 1,
      };
    },
    {
      variables: {
        meId,
        contactId,
        isUserPreview,
        teamId,
        isPreviewTeamSwitchPending,
      },
      skip:
        !contactId ||
        (isUserPreview && (isPreviewTeamSwitchPending || !teamId)),
      teamScoped: false,
    }
  );

  const queryData = useInfiniteQuery(
    (q, args, { limit, offset }) => {
      if (
        args.isUserPreview &&
        (args.isPreviewTeamSwitchPending || !args.teamId)
      )
        return {
          list: [],
          aggregate: 0,
        };

      const where: gqlV2.owner_user_access_bool_exp =
        args.isUserPreview && args.teamId
          ? {
              userId: { _eq: args.meId },
              _or: args.search
                ? [
                    {
                      owner: {
                        tenantId: { _eq: args.teamId },
                        name: { _ilike: `%${args.search}%` },
                      },
                    },
                    {
                      owner: {
                        tenantId: { _eq: args.teamId },
                        firstName: { _ilike: `%${args.search}%` },
                      },
                    },
                    {
                      contact: {
                        tenantId: { _eq: args.teamId },
                        name: { _ilike: `%${args.search}%` },
                      },
                    },
                    {
                      contact: {
                        tenantId: { _eq: args.teamId },
                        firstName: { _ilike: `%${args.search}%` },
                      },
                    },
                  ]
                : [
                    {
                      owner: {
                        tenantId: { _eq: args.teamId },
                      },
                    },
                    {
                      contact: {
                        tenantId: { _eq: args.teamId },
                      },
                    },
                  ],
            }
          : {
              userId: { _eq: args.meId },
              _or: args.search
                ? [
                    {
                      owner: {
                        name: { _ilike: `%${args.search}%` },
                      },
                    },
                    {
                      owner: {
                        firstName: { _ilike: `%${args.search}%` },
                      },
                    },
                    {
                      contact: {
                        name: { _ilike: `%${args.search}%` },
                      },
                    },
                    {
                      contact: {
                        firstName: { _ilike: `%${args.search}%` },
                      },
                    },
                  ]
                : undefined,
            };

      const l = q
        .ownerUserAccesses({
          where,
          limit,
          offset,
        })
        .map(getOwner);

      const list = sortBy(l, 'label');

      return {
        list: groupNavbarOwnerSwitchItems(list),
        aggregate: list.length,
      };
    },
    {
      queryKey: 'teams',
      skip:
        !shouldFetchOptions ||
        (isUserPreview && (isPreviewTeamSwitchPending || !teamId)),
      variables: {
        search: search?.trim(),
        meId,
        isUserPreview,
        teamId,
        isPreviewTeamSwitchPending,
      },
      pageLimit: 1000,
    }
  );

  return {
    queryData,
    value: valueData?.contact,
    loadingValue,
    canSwitch: valueData?.canSwitch ?? false,
  };
}

const OwnerSwitch = () => {
  const { actor } = useAuth();
  const [searchParams] = useSearchParams();

  const { appName } = useAppName();

  const [team] = useTeam();
  const { teamId: selectedTeamId } = useTeamIdContext();
  const setTeamId = useTeamSwitch();
  const goto = useNavigate();

  const [search, setSearch] = useState('');
  const isUserPreview = !!actor?.sub;
  const sti = searchParams.get('sti');
  const teamScopeId = selectedTeamId ?? team?.id;
  const isPreviewTeamSwitchPending =
    isUserPreview && !!sti && isUUID(sti) && sti !== teamScopeId;

  const { contactId, setContactId } = useOwnerPortalContact();
  const gate = useSelectQueryGate({ value: contactId });

  const { queryData, canSwitch, loadingValue, value } = useOwnerQuery(search, {
    shouldFetchOptions: gate.shouldFetchOptions,
    isUserPreview,
    teamId: teamScopeId,
    isPreviewTeamSwitchPending,
  });

  const setNewOwner = useCallback(
    (newValue: SelectItem | null) => {
      if (isPreviewTeamSwitchPending) return;
      if (!newValue?.value) return;

      const tenantId = queryData.data?.pages
        .flatMap((x) => x.list)
        .find((x) => x.value === newValue?.value)?.tenantId;

      if (!tenantId) return;
      if (isUserPreview && teamScopeId && tenantId !== teamScopeId) return;

      if (!isUserPreview) {
        setTeamId(tenantId);
      }
      setContactId(newValue.value);

      const hasKeys = [...searchParams.values()].filter(hasValue).length > 0;
      if (hasKeys && !isPreviewTeamSwitchPending) {
        goto(window.location.pathname);
      }
    },
    [
      isPreviewTeamSwitchPending,
      isUserPreview,
      teamScopeId,
      setTeamId,
      searchParams,
      goto,
      setContactId,
      queryData,
    ]
  );

  return (
    <NavbarTeamSwitchSelect
      value={value ?? null}
      canSwitch={!isPreviewTeamSwitchPending && canSwitch}
      loadingQuery={loadingValue || isPreviewTeamSwitchPending}
      onOpenChange={gate.onOpenChange}
      queryData={queryData}
      header={team?.name || appName}
      setSearch={setSearch}
      setValue={setNewOwner}
    />
  );
};

export const NavbarTeamSwitchSelect = ({
  header,
  setSearch,
  setValue,
  customActionTop,
  customBottomActions,
  pinnedItems,
  value,
  canSwitch,
  loadingQuery,
  queryData,
  onOpenChange,
  isDesktopExpanded: isDesktopExpandedOverride,
  renderTrigger,
  tone = 'navbar',
}: {
  header?: string | null;
  setSearch: (search: string) => void;
  setValue: (value: SelectItem | null) => void;
  customActionTop?: SelectProps<string>['customActionTop'];
  customBottomActions?: SelectProps<string>['customBottomActions'];
  pinnedItems?: SelectProps<string>['pinnedItems'];
  value: SelectItem | null;
  loadingQuery: boolean;
  canSwitch: boolean;
  queryData: Omit<
    NonNullable<SelectProps<string>['infiniteData']>,
    'setSearch'
  >;
  onOpenChange?: (opened: boolean) => void;
  isDesktopExpanded?: boolean;
  renderTrigger?: (props: NavbarTeamSwitchTriggerProps) => JSX.Element;
  tone?: 'navbar' | 'default';
}) => {
  const { isDesktopExpanded: navbarExpanded } = useNavbarExpanded();
  const { colorScheme } = useMantineColorScheme();
  const { colors } = useMantineTheme();
  const isDesktopExpanded = isDesktopExpandedOverride ?? navbarExpanded;
  const isLightSurface = tone === 'default';
  const foregroundColor = !isLightSurface
    ? '#fff'
    : colorScheme === 'dark'
      ? colors.neutral[0]
      : colors.neutral[9];

  return (
    <Stack
      gap={0}
      pt={5}
      sx={{
        color: foregroundColor,
        width: '100%',
      }}
    >
      {!!header && isDesktopExpanded && (
        <Text sx={{ opacity: 0.8 }} size="xs">
          {header}
        </Text>
      )}
      <Select
        type="single"
        setValue={setValue}
        infiniteData={{ ...queryData, setSearch }}
        dropdownProps={{
          withinPortal: true,
          position: isDesktopExpanded ? 'bottom' : 'bottom-start',
          width: 260,
        }}
        customActionTop={customActionTop}
        customBottomActions={customBottomActions}
        pinnedItems={pinnedItems}
        onOpenChange={onOpenChange}
        inputProps={{
          loadingQuery,
        }}
        value={value}
      >
        {({ opened, value }) => {
          const triggerProps = {
            opened,
            canSwitch: !!canSwitch,
            isNavigationExpanded: isDesktopExpanded,
            label: (value as any)?.label,
            tone,
          } satisfies NavbarTeamSwitchTriggerProps;

          return (
            renderTrigger?.(triggerProps) || <PopoverButton {...triggerProps} />
          );
        }}
      </Select>
    </Stack>
  );
};

export type NavbarTeamSwitchTriggerProps = {
  opened: boolean;
  label?: string;
  isNavigationExpanded: boolean;
  canSwitch: boolean;
  tone?: 'navbar' | 'default';
};

const PopoverButton = forwardRef<HTMLDivElement, NavbarTeamSwitchTriggerProps>(
  ({ isNavigationExpanded, canSwitch, label, tone = 'navbar' }, ref) => {
    const [{ isGL: GL }] = useTeam();
    const { colorScheme } = useMantineColorScheme();
    const { colors } = useMantineTheme();
    const isLightSurface = tone === 'default';
    const textColor = !isLightSurface
      ? undefined
      : colorScheme === 'dark'
        ? colors.neutral[0]
        : colors.neutral[9];
    const hoverBackground = !isLightSurface
      ? '#5C617840'
      : colorScheme === 'dark'
        ? colors.dark[4]
        : colors.neutral[1];
    const chevronColor = !isLightSurface
      ? '#ffffff80'
      : colorScheme === 'dark'
        ? colors.neutral[4]
        : colors.neutral[5];

    return (
      <Group
        wrap="nowrap"
        gap={0}
        ref={ref}
        pl={isNavigationExpanded ? 8 : 0}
        ml={isNavigationExpanded ? -8 : 0}
        sx={{
          borderRadius: 7,
          maxWidth: GL ? 140 : 160,
          width: '100%',
          justifyContent: isNavigationExpanded ? 'space-between' : 'center',
          display: !isNavigationExpanded && !canSwitch ? 'none' : undefined,
          cursor: canSwitch ? 'pointer' : 'default',
          ':hover': {
            backgroundColor: canSwitch ? hoverBackground : undefined,
          },
          '&, *': {
            overflowY: 'hidden',
          },
        }}
      >
        {isNavigationExpanded && (
          <Group gap={5} wrap="nowrap" pt={4} sx={{ height: '100%' }}>
            <Text
              component="span"
              sx={{
                maxWidth: '100%',
                display: 'block',
                height: 20,
                textOverflow: 'ellipsis',
                overflowX: 'hidden',
                whiteSpace: 'nowrap',
                lineHeight: 'normal',
                color: textColor,
              }}
            >
              {label}
            </Text>
          </Group>
        )}

        {canSwitch && (
          <IconButton
            component="span"
            sx={{
              ':hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            <ChevronIcon size={17} color={chevronColor} />
          </IconButton>
        )}
      </Group>
    );
  }
);
